import { zodResolver } from '@hookform/resolvers/zod';
import { format, toZonedTime } from 'date-fns-tz';
import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Platform, RefreshControl, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Modal from 'react-native-modal';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';
import { z } from 'zod';

import type { AdditionalDriver } from '@/api/reservations/types';
import type { AdditionalDriverPayment } from '@/api/reservations/use-add-additional-driver';
import {
  AdditionalDriverInviteErrors,
  AdditionalDriverPaymentRequired,
  useAddAdditionalDriver,
} from '@/api/reservations/use-add-additional-driver';
import { useResendAdditionalDriver } from '@/api/reservations/use-resend-additional-driver';
import { useReservation } from '@/api/reservations/use-reservation';
import { AdditionalDriverRow } from '@/components/additional-drivers/drivers-card';
import { AdditionalDriverPaymentForm } from '@/components/additional-drivers/payment';
import BackButton from '@/components/back-button';
import ContactSupportModal from '@/components/contact-support-modal';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { formatNumber, isDesktop, useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, Card, ControlledInput, showErrorMessage, Text } from '@/ui';
import { Chip, ChipType } from '@/ui/chip';
import { Loader } from '@/ui/loader';

const addDriverFormSchema = z.object({
  email: z.string().email(),
});

export type AddDriverFormType = z.infer<typeof addDriverFormSchema>;

const renderBackButton = () => <BackButton />;

export default function AdditionalDrivers() {
  const { id } = useLocalSearchParams();
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const status = useAuth.use.status();
  const [stagedEmails, setStagedEmails] = React.useState<string[]>([]);
  const [inviteErrors, setInviteErrors] = React.useState<
    Record<string, string>
  >({});
  const [feeEmail, setFeeEmail] = React.useState<string | null>(null);
  const [selectedDriver, setSelectedDriver] =
    React.useState<AdditionalDriver | null>(null);
  const [payment, setPayment] = React.useState<AdditionalDriverPayment | null>(
    null
  );
  const [receiptEmails, setReceiptEmails] = React.useState<string[] | null>(
    null
  );
  const [contactSupport, setContactSupport] = React.useState<
    'pending' | 'open' | null
  >(null);

  const {
    data: resData,
    isPending: isFetchingReservation,
    refetch,
  } = useReservation({
    //@ts-ignore
    variables: { id: id },
    enabled: !!id,
  });

  const {
    mutateAsync: addAdditionalDrivers,
    isPending: isAddingAdditionalDrivers,
  } = useAddAdditionalDriver();
  const { mutateAsync: resendAdditionalDriver, isPending: isResending } =
    useResendAdditionalDriver();
  const {
    control,
    trigger,
    setValue,
    formState: { isValid },
    getValues,
    reset,
  } = useForm<AddDriverFormType>({
    resolver: zodResolver(addDriverFormSchema),
  });

  useFocusEffect(
    React.useCallback(() => {
      refetch();
    }, [refetch])
  );

  useEffect(() => {
    if (status === 'signIn') {
      refetch();
    }
  }, [status, refetch]);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Additional Driver',
    });
  }, []);

  if (isFetchingReservation || !resData) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  const pricing = resData.additionalDriverPricing;
  const invitedCount = resData.additionalDrivers.length;
  const freeDriversLeft = Math.max(
    0,
    pricing.freeDrivers - invitedCount - stagedEmails.length
  );
  const feePerDriver = pricing.dailyRate * pricing.days;
  // Order is the price: invited drivers use up the free slots first.
  const paidEmails = stagedEmails.filter(
    (_, index) => invitedCount + index >= pricing.freeDrivers
  );
  const totalDue = paidEmails.length * feePerDriver;
  // The staged list must not change between the 402 and the paid retry.
  const isCheckingOut = !!payment || isAddingAdditionalDrivers;

  const enteredEmail = (getValues().email ?? '').trim().toLowerCase();
  const isEmailTaken =
    enteredEmail === resData.customer.email.toLowerCase() ||
    stagedEmails.includes(enteredEmail) ||
    resData.additionalDrivers.some(
      (driver) => driver.email.toLowerCase() === enteredEmail
    );

  const stageEmail = (email: string) => {
    setStagedEmails((emails) => [...emails, email]);
    setFeeEmail(null);
    reset({ email: '' });
  };

  const sendInvites = async (paymentIntentId?: string) => {
    try {
      await addAdditionalDrivers({
        id: resData.id,
        emails: stagedEmails,
        paymentIntentId,
      });

      const sentEmails = stagedEmails;
      setStagedEmails([]);
      setInviteErrors({});
      await refetch();

      if (paymentIntentId) {
        setReceiptEmails(sentEmails);
      } else {
        Toast.show({
          text1: `Invite sent to ${sentEmails.join(', ')}`,
          type: 'success',
        });
      }
    } catch (e: any) {
      if (e instanceof AdditionalDriverPaymentRequired) {
        setPayment(e.payment);
        return;
      }

      if (e instanceof AdditionalDriverInviteErrors) {
        setInviteErrors(e.errorsByEmail);
        return;
      }

      showErrorMessage(e.message);
    }
  };

  const handlePaymentSucceeded = async () => {
    if (!payment) return;

    const paymentIntentId = payment.paymentIntentId;
    setPayment(null);
    await sendInvites(paymentIntentId);
  };

  const title = receiptEmails ? 'Invites Sent' : 'Additional Driver';
  const zonedDate = (time: string) =>
    format(toZonedTime(time, resData.pickupLocation.timezone), 'MMM d');

  return (
    <View
      style={{
        flex: 1,
        gap: 32,
        alignItems: 'center',
        backgroundColor: theme.colors.background_1,
      }}
    >
      {Platform.OS === 'web' && isDesktop() ? (
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />
      ) : (
        <Stack.Screen
          options={{
            title,
            headerTitle: title,
            headerLeft: receiptEmails ? undefined : renderBackButton,
            headerBackVisible: false,
            headerShown: true,
            headerStyle: {
              backgroundColor: theme.colors.background_1,
            },
            headerTintColor: theme.colors.text.primary,
            headerShadowVisible: false,
            headerTitleStyle: {
              fontSize: 17,
            },
          }}
        />
      )}

      <View style={styles.container}>
        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <View style={{ gap: 16 }}>
            {receiptEmails ? null : (
              <NavigationButton
                type={NavigationButtonTypes.back_with_text}
                onPress={() => {
                  router.back();
                }}
              />
            )}

            <Text type="largeTitle">{title}</Text>
          </View>
        ) : null}

        <KeyboardAwareScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          style={styles.scrollView}
          refreshControl={
            <RefreshControl
              refreshing={isFetchingReservation}
              onRefresh={() => {
                refetch();
              }}
            />
          }
        >
          {receiptEmails ? (
            <Card>
              <View style={styles.cardHeader}>
                <Text type="title2">Receipt</Text>
              </View>

              <View style={styles.cardBody}>
                {receiptEmails.map((email) => (
                  <View key={email} style={styles.lineRow}>
                    <Text type="bodyRegular" style={styles.secondaryText}>
                      Driver
                    </Text>
                    <Text type="bodyRegular">{email}</Text>
                  </View>
                ))}

                <View style={styles.lineRow}>
                  <Text type="bodyRegular" style={styles.secondaryText}>
                    Rental Period
                  </Text>
                  <Text type="bodyRegular">
                    {`${zonedDate(resData.startTime)} - ${zonedDate(
                      resData.endTime
                    )}`}
                  </Text>
                </View>
              </View>

              <View style={[styles.lineRow, styles.cardFooter]}>
                <Text type="bodyRegular">Total</Text>
                <Text type="bodyRegular">
                  {`$${formatNumber(
                    resData.additionalDrivers
                      .filter((driver) =>
                        receiptEmails.includes(driver.email.toLowerCase())
                      )
                      .reduce((sum, driver) => sum + (driver.feeAmount ?? 0), 0)
                  )}`}
                </Text>
              </View>
            </Card>
          ) : (
            <View style={{ gap: 24 }}>
              <View style={{ gap: 8 }}>
                <Text type="title2">Driver & Insurance</Text>

                <Text type="bodyRegular" style={styles.secondaryText}>
                  {`Drivers must be 25+, verified, and on your insurance policy. ${pricing.freeDrivers} are free, then $${pricing.dailyRate}/day each.`}
                </Text>
              </View>

              <Card>
                <View style={[styles.cardHeader, styles.spaceBetween]}>
                  <Text type="title2">Additional Drivers</Text>

                  <Chip
                    type={ChipType.BRAND}
                    text={`${freeDriversLeft} free left`}
                    iconWidth={8}
                    iconHeight={8}
                    style={{ alignSelf: 'center' }}
                  />
                </View>

                <View style={styles.cardBody}>
                  {resData.additionalDrivers.map((driver) => (
                    <AdditionalDriverRow
                      key={driver.email}
                      driver={driver}
                      statusBelow
                      onPressInvite={() => {
                        setSelectedDriver(driver);
                      }}
                    />
                  ))}

                  {stagedEmails.map((email) => (
                    <AdditionalDriverRow
                      key={email}
                      driver={{ email, status: 'Not sent yet' }}
                      error={inviteErrors[email]}
                      statusBelow
                      onRemove={
                        isCheckingOut
                          ? undefined
                          : () => {
                              setStagedEmails((emails) =>
                                emails.filter((e) => e !== email)
                              );
                            }
                      }
                    />
                  ))}

                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    <ControlledInput
                      control={control}
                      name="email"
                      label=""
                      placeholder="Email address"
                      spellCheck={false}
                      autoComplete="email"
                      containerStyle={{ flex: 1 }}
                      onBlur={() => {
                        trigger('email');
                      }}
                      onChangeText={(text) => {
                        trigger('email');
                        setValue('email', text);
                      }}
                    />

                    <Button
                      text="Add"
                      type="secondary"
                      style={{ width: 'auto' }}
                      disabled={!isValid || isEmailTaken || isCheckingOut}
                      onPress={() => {
                        if (freeDriversLeft === 0) {
                          setFeeEmail(enteredEmail);
                          return;
                        }

                        stageEmail(enteredEmail);
                      }}
                    />
                  </View>
                </View>
              </Card>

              {paidEmails.length > 0 ? (
                <Card>
                  <View style={styles.cardHeader}>
                    <Text type="title2">Payment Summary</Text>
                  </View>

                  <View style={styles.cardBody}>
                    {paidEmails.map((email) => (
                      <View key={email} style={styles.lineRow}>
                        <View style={{ flex: 1 }}>
                          <Text type="bodyRegular">Additional Driver Fee</Text>
                          <Text type="caption1" style={styles.secondaryText}>
                            {email}
                          </Text>
                          <Text type="caption1" style={styles.secondaryText}>
                            {`$${pricing.dailyRate}/day x ${pricing.days} days`}
                          </Text>
                        </View>
                        <Text type="bodyRegular">{`$${formatNumber(
                          feePerDriver
                        )}`}</Text>
                      </View>
                    ))}
                  </View>

                  <View style={[styles.lineRow, styles.cardFooter]}>
                    <Text type="bodyRegular">Total Due Now</Text>
                    <Text type="bodyRegular">{`$${formatNumber(
                      totalDue
                    )}`}</Text>
                  </View>
                </Card>
              ) : null}

              {payment ? (
                <Card style={{ padding: 16, gap: 16 }}>
                  <Text type="title2">Payment</Text>
                  <AdditionalDriverPaymentForm
                    payment={payment}
                    payer={{
                      name: resData.customer.name,
                      phone: resData.customer.phone,
                      email: resData.customer.email,
                    }}
                    onSucceeded={handlePaymentSucceeded}
                    onFailed={() => {
                      setPayment(null);
                    }}
                  />
                </Card>
              ) : null}
            </View>
          )}
        </KeyboardAwareScrollView>

        {receiptEmails ? (
          <Button
            text="Back To Trip"
            type="primary"
            onPress={() => {
              router.replace(`/trips/${id}/details`);
            }}
            style={styles.bottomButton}
          />
        ) : stagedEmails.length > 0 && !payment ? (
          <Button
            text={
              totalDue > 0
                ? `Pay $${formatNumber(totalDue)} & Send Invites`
                : 'Send Invite'
            }
            type="primary"
            loading={isAddingAdditionalDrivers}
            disabled={isAddingAdditionalDrivers}
            onPress={() => {
              sendInvites();
            }}
            style={styles.bottomButton}
          />
        ) : null}
      </View>

      <DriverDialog
        title="Additional Driver Fee"
        isVisible={!!feeEmail}
        onClose={() => {
          setFeeEmail(null);
        }}
      >
        <Text type="bodyRegular">
          {`You've used your ${
            pricing.freeDrivers
          } free drivers. The next driver costs $${
            pricing.dailyRate
          }/day ($${formatNumber(feePerDriver)} for this trip).`}
        </Text>

        <Button
          text="Add Driver"
          type="primary"
          onPress={() => {
            if (feeEmail) {
              stageEmail(feeEmail);
            }
          }}
        />
      </DriverDialog>

      <DriverDialog
        title="Invite Sent"
        isVisible={!!selectedDriver}
        onClose={() => {
          setSelectedDriver(null);
        }}
        onModalHide={() => {
          if (contactSupport === 'pending') setContactSupport('open');
        }}
      >
        <Text type="bodyRegular">
          {`Waiting for ${
            selectedDriver?.name || selectedDriver?.email
          } to accept. To remove this driver, contact support.`}
        </Text>

        <Button
          text="Resend Invite"
          type="primary"
          disabled={isResending}
          onPress={async () => {
            if (!selectedDriver) return;

            try {
              await resendAdditionalDriver({
                id: resData.id,
                email: selectedDriver.email,
              });
              setSelectedDriver(null);
              Toast.show({
                text1: `Invite sent to ${selectedDriver.email}`,
                type: 'success',
              });
            } catch (e: any) {
              showErrorMessage(e.message);
            }
          }}
        />

        <Button
          text="Contact Support"
          type="ghost"
          onPress={() => {
            setContactSupport('pending');
            setSelectedDriver(null);
          }}
        />
      </DriverDialog>

      <ContactSupportModal
        isVisible={contactSupport === 'open'}
        onDismiss={() => {
          setContactSupport(null);
        }}
      />
    </View>
  );
}

function DriverDialog({
  title,
  isVisible,
  onClose,
  onModalHide,
  children,
}: {
  title: string;
  isVisible: boolean;
  onClose: () => void;
  onModalHide?: () => void;
  children: React.ReactNode;
}) {
  const { styles } = useStyles(stylesheet);

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={1000}
      isVisible={isVisible}
      hasBackdrop
      style={styles.modalContainer}
      onBackdropPress={onClose}
      onModalHide={onModalHide}
    >
      <View style={styles.modal}>
        <View style={styles.spaceBetween}>
          <Text type="title2">{title}</Text>
          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={onClose}
          />
        </View>

        {children}
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    width: {
      xs: '100%',
      md: 'auto',
    },
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    gap: 16,
    backgroundColor: theme.colors.background_1,
  },
  scrollView: {
    width: '100%',
    flex: 1,
  },
  scrollContent: {
    width: {
      xs: '100%',
      md: 600,
    },
    alignSelf: 'center',
    paddingBottom: 16,
  },
  secondaryText: {
    color: theme.colors.text.secondary,
  },
  spaceBetween: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardHeader: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  cardBody: {
    padding: 16,
    gap: 16,
  },
  cardFooter: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.card.default.borderColor,
  },
  lineRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  bottomButton: {
    alignSelf: 'center',
    marginBottom: 16,
    width: {
      xs: '100%',
      md: 600,
    },
  },
  modalContainer: {
    margin: 0,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 600,
    },
    width: {
      xs: '100%',
      md: 700,
    },
  },
  modal: {
    width: '90%',
    backgroundColor: theme.colors.background_2,
    borderRadius: 16,
    padding: 16,
    gap: 16,
  },
}));
